import {
  FC,
  ReactNode,
  useMemo,
  isValidElement,
  useRef,
  useEffect,
} from "react";
import { Wrapper, Error, StyledInput, Label, IconBox } from "./Input.styled";
import { FormInputProps } from "@otrium/atoms";
import NoSSR from "../NoSSR";
import { INVALID_EMAIL_WARNING } from "src/organisms/CheckoutView/constants";
import { Trans } from "@lingui/macro";
import { useBreakpoints } from "@otrium/core";

interface Props {
  isError?: boolean;
  errorMessage?: string | ReactNode;
  rightIcon?: ReactNode;
  label?: string;
  fullWidth?: boolean;
  inputTestId?: string;
}

const Input: FC<Props & FormInputProps> = ({
  isError,
  errorMessage,
  label,
  fullWidth = false,
  rightIcon,
  inputTestId = "input",
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useBreakpoints();

  const isWarning: boolean = useMemo(() => {
    if (
      isValidElement(errorMessage) &&
      "children" in errorMessage.props &&
      typeof errorMessage?.props?.children === "string" &&
      errorMessage?.props?.children === INVALID_EMAIL_WARNING
    ) {
      return true;
    }

    return false;
  }, [errorMessage]);

  useEffect(() => {
    if (!inputRef.current || !isWarning) {
      return;
    }

    if (isMobile) {
      window.scrollBy({
        top: inputRef.current.getBoundingClientRect().top - 80,
      });
    }
  }, [isWarning, isMobile]);

  return (
    <Wrapper
      hasValue={!!(rightIcon && rest.value?.toString().length)}
      disabled={rest.disabled}
      fullWidth={fullWidth}
      data-testid="input-wrapper"
    >
      {label && (
        <Label as="label" px={1} data-testid="input-label">
          {label}
        </Label>
      )}
      <NoSSR>
        {/* @ts-expect-error Fix types issue in the DS */}
        <StyledInput
          error={(isError || !!errorMessage) && !isWarning}
          warning={isWarning}
          rightIcon={!!rightIcon}
          {...rest}
          data-testid={inputTestId}
          ref={inputRef}
        />
      </NoSSR>
      {rightIcon && <IconBox data-testid="input-icon-box">{rightIcon}</IconBox>}
      {errorMessage && (
        <Error data-testid="input-error" isWarning={isWarning}>
          {isWarning ? <Trans>INVALID_EMAIL_WARNING</Trans> : errorMessage}
        </Error>
      )}
    </Wrapper>
  );
};

export default Input;
